import React, { useCallback, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { differenceInMinutes, eachDayOfInterval, format, parseISO, subDays } from "date-fns"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarIcon,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  ExternalLink,
  Filter,
  PackageCheck,
  Target,
  TrendingUp,
} from "lucide-react"
import { DateRange } from "react-day-picker"
import { useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/shared/EmptyState"
import { LoadingState } from "@/components/shared/LoadingState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { buildCsv, downloadCsv } from "@/lib/csvExport"
import { formatDayMonth, formatDayMonthYear } from "@/lib/dateFormats"
import { getRouteInstanceStatusTone } from "@/lib/statusDisplay"
import { supabase } from "@/lib/supabase"
import { toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import { RouteInstance, StopInstance } from "@/types/collections"

interface StopRow {
  id: string
  sequence: number
  name: string
  stopType: string
  plannedArrival: string | null
  actualArrival: string | null
  delayMin: number | null
}

interface RoutePerformanceRow {
  routeInstanceId: string
  date: string
  dayOfWeek: string
  routeCode: string
  depot: string
  status: string
  vehicleReg: string
  plannedStart: string | null
  plannedEnd: string | null
  plannedDurationMin: number | null
  actualStart: string | null
  actualEnd: string | null
  actualDurationMin: number | null
  durationDeltaMin: number | null
  hasDepotInjection: boolean
  plannedInjectionTime: string | null
  actualInjectionTime: string | null
  injectionDelayMin: number | null
  totalShipperStops: number
  shipperStopsWithActual: number
  ota15Count: number
  ota30Count: number
  ota15Pct: number | null
  ota30Pct: number | null
  stops: StopRow[]
}

interface SummaryStats {
  totalRoutes: number
  routesWithActuals: number
  avgDurationDeltaMin: number | null
  overallOta15Pct: number | null
  overallOta30Pct: number | null
  injectionRoutesWithActual: number
  injectionLate15: number
}

type SortColumn =
  | "date"
  | "route"
  | "depot"
  | "plannedDuration"
  | "actualDuration"
  | "durationDelta"
  | "injectionDelay"
  | "ota15"
  | "ota30"
  | "status"

function formatTime(iso: string | null | undefined): string {
  if (!iso) return "--"
  try {
    return format(parseISO(iso), "HH:mm")
  } catch {
    return "--"
  }
}

function formatMinutes(min: number | null): string {
  if (min === null || min === undefined) return "--"
  const hrs = Math.floor(Math.abs(min) / 60)
  const mins = Math.round(Math.abs(min) % 60)
  const sign = min < 0 ? "-" : ""
  return hrs > 0 ? `${sign}${hrs}h ${mins}m` : `${sign}${mins}m`
}

function getDeltaColor(deltaMin: number | null): string {
  if (deltaMin === null) return "text-palette-jungleGreen400"
  if (deltaMin <= 0) return "text-green-600 font-medium"
  if (deltaMin <= 15) return "text-yellow-600 font-medium"
  return "text-red-600 font-medium"
}

/** Shared by the filter, tile and CSV, so the table's row count always matches the tile. */
const INJECTION_LATE_THRESHOLD_MIN = 15

function isInjectionLate(row: RoutePerformanceRow): boolean {
  return row.injectionDelayMin !== null && row.injectionDelayMin >= INJECTION_LATE_THRESHOLD_MIN
}

function getLateCountColor(count: number): string {
  return count > 0 ? "text-red-600" : "text-green-600"
}

function getOtaColor(pct: number | null): string {
  if (pct === null) return "text-palette-jungleGreen400"
  if (pct >= 90) return "text-green-600 font-medium"
  if (pct >= 75) return "text-yellow-600 font-medium"
  return "text-red-600 font-medium"
}

function getStatusBadge(status: string) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-[10px] px-1.5 py-0",
        toneClasses(getRouteInstanceStatusTone(status)).badge,
      )}
    >
      {status.replace("_", " ")}
    </Badge>
  )
}

function getStopDelay(stop: StopInstance): number | null {
  if (stop.delay_minutes !== undefined && stop.delay_minutes !== null && stop.delay_minutes !== 0) {
    return stop.delay_minutes
  }
  if (stop.actual_arrival && stop.planned_arrival) {
    return differenceInMinutes(parseISO(stop.actual_arrival), parseISO(stop.planned_arrival))
  }
  return null
}

const RoutePerformance: React.FC = () => {
  const navigate = useNavigate()

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 1),
    to: subDays(new Date(), 1),
  })
  const [depotFilter, setDepotFilter] = useState<string[]>([])
  const [sortColumn, setSortColumn] = useState<SortColumn>("date")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc")

  const datesToFetch = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return []
    return eachDayOfInterval({ start: dateRange.from, end: dateRange.to })
      .slice(0, 31)
      .map((d) => format(d, "yyyy-MM-dd"))
  }, [dateRange])

  const { data: routeInstances, isLoading } = useQuery({
    queryKey: ["collections", "route_instances", "route-performance", datesToFetch],
    queryFn: async () => {
      const allInstances: RouteInstance[] = []

      for (const dateStr of datesToFetch) {
        try {
          const { data, error } = await supabase.functions.invoke("collections", {
            body: {
              action: "get_route_instances_for_date",
              date: dateStr,
            },
          })

          if (!error && data?.success && data?.data) {
            allInstances.push(...(data.data as RouteInstance[]))
          }
        } catch (err) {
          console.error(`Error fetching route instances for ${dateStr}:`, err)
        }
      }

      return allInstances
    },
    enabled: datesToFetch.length > 0,
    staleTime: 60000,
  })

  const availableDepots = ["HIVED Hayes Depot", "HIVED Silvertown Depot"]

  const rows: RoutePerformanceRow[] = useMemo(() => {
    if (!routeInstances?.length) return []

    return routeInstances.map((ri): RoutePerformanceRow => {
      // Overnight routes store the end on the start's date, so a negative duration gets +24h.
      const calcDuration = (start: string, end: string): number => {
        const diff = differenceInMinutes(parseISO(end), parseISO(start))
        return diff < 0 ? diff + 24 * 60 : diff
      }
      const plannedDurationMin =
        ri.planned_start_time && ri.planned_end_time
          ? calcDuration(ri.planned_start_time, ri.planned_end_time)
          : null
      const actualDurationMin =
        ri.actual_start_time && ri.actual_end_time
          ? calcDuration(ri.actual_start_time, ri.actual_end_time)
          : null
      const durationDeltaMin =
        actualDurationMin !== null && plannedDurationMin !== null
          ? actualDurationMin - plannedDurationMin
          : null

      const stops = ri.stops || []
      const depotInjectionStop = stops.find((s) => s.stop_type === "depot_injection")
      const plannedInjectionTime = depotInjectionStop?.planned_arrival || null
      const actualInjectionTime = depotInjectionStop?.actual_arrival || null
      const injectionDelayMin = depotInjectionStop ? getStopDelay(depotInjectionStop) : null

      const shipperStops = stops.filter(
        (s) => s.stop_type === "shipper_collection" || s.stop_type === "haulage_collection",
      )
      const totalShipperStops = shipperStops.length
      let shipperStopsWithActual = 0
      let ota15Count = 0
      let ota30Count = 0

      for (const stop of shipperStops) {
        const delay = getStopDelay(stop)
        if (delay !== null) {
          shipperStopsWithActual++
          if (delay <= 15) ota15Count++
          if (delay <= 30) ota30Count++
        }
      }

      const ota15Pct =
        shipperStopsWithActual > 0 ? (ota15Count / shipperStopsWithActual) * 100 : null
      const ota30Pct =
        shipperStopsWithActual > 0 ? (ota30Count / shipperStopsWithActual) * 100 : null

      const stopRows: StopRow[] = stops
        .slice()
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
        .map((s) => ({
          id: s.id,
          sequence: s.sequence,
          name: s.name,
          stopType: s.stop_type,
          plannedArrival: s.planned_arrival || null,
          actualArrival: s.actual_arrival || null,
          delayMin: getStopDelay(s),
        }))

      return {
        routeInstanceId: ri.id,
        date: ri.date,
        dayOfWeek: format(parseISO(ri.date), "EEEE"),
        routeCode: ri.code,
        depot: ri.depot,
        status: ri.status,
        vehicleReg: ri.vehicle_reg || "",
        plannedStart: ri.planned_start_time,
        plannedEnd: ri.planned_end_time,
        plannedDurationMin,
        actualStart: ri.actual_start_time || null,
        actualEnd: ri.actual_end_time || null,
        actualDurationMin,
        durationDeltaMin,
        hasDepotInjection: depotInjectionStop !== undefined,
        plannedInjectionTime,
        actualInjectionTime,
        injectionDelayMin,
        totalShipperStops,
        shipperStopsWithActual,
        ota15Count,
        ota30Count,
        ota15Pct,
        ota30Pct,
        stops: stopRows,
      }
    })
  }, [routeInstances])

  const filteredRows = useMemo(() => {
    let result = depotFilter.length > 0 ? rows.filter((r) => depotFilter.includes(r.depot)) : rows

    result = [...result].sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1
      switch (sortColumn) {
        case "date":
          return (a.date.localeCompare(b.date) || a.routeCode.localeCompare(b.routeCode)) * dir
        case "route":
          return a.routeCode.localeCompare(b.routeCode) * dir
        case "depot":
          return a.depot.localeCompare(b.depot) * dir
        case "plannedDuration":
          return ((a.plannedDurationMin ?? 0) - (b.plannedDurationMin ?? 0)) * dir
        case "actualDuration":
          return ((a.actualDurationMin ?? 9999) - (b.actualDurationMin ?? 9999)) * dir
        case "durationDelta":
          return ((a.durationDeltaMin ?? 9999) - (b.durationDeltaMin ?? 9999)) * dir
        case "injectionDelay":
          return ((a.injectionDelayMin ?? 9999) - (b.injectionDelayMin ?? 9999)) * dir
        case "ota15":
          return ((a.ota15Pct ?? -1) - (b.ota15Pct ?? -1)) * dir
        case "ota30":
          return ((a.ota30Pct ?? -1) - (b.ota30Pct ?? -1)) * dir
        case "status":
          return a.status.localeCompare(b.status) * dir
        default:
          return 0
      }
    })

    return result
  }, [rows, depotFilter, sortColumn, sortDirection])

  const summary: SummaryStats = useMemo(() => {
    const withActuals = filteredRows.filter((r) => r.actualDurationMin !== null)
    const deltas = withActuals.map((r) => r.durationDeltaMin).filter((d): d is number => d !== null)
    const totalShipper = filteredRows.reduce((acc, r) => acc + r.shipperStopsWithActual, 0)
    const totalOta15 = filteredRows.reduce((acc, r) => acc + r.ota15Count, 0)
    const totalOta30 = filteredRows.reduce((acc, r) => acc + r.ota30Count, 0)
    const injectionWithActual = filteredRows.filter(
      (r) => r.hasDepotInjection && r.injectionDelayMin !== null,
    )

    return {
      totalRoutes: filteredRows.length,
      routesWithActuals: withActuals.length,
      avgDurationDeltaMin:
        deltas.length > 0 ? deltas.reduce((a, b) => a + b, 0) / deltas.length : null,
      overallOta15Pct: totalShipper > 0 ? (totalOta15 / totalShipper) * 100 : null,
      overallOta30Pct: totalShipper > 0 ? (totalOta30 / totalShipper) * 100 : null,
      injectionRoutesWithActual: injectionWithActual.length,
      injectionLate15: injectionWithActual.filter(isInjectionLate).length,
    }
  }, [filteredRows])

  const toggleRow = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleOpenInMonitoring = useCallback(
    (date: string, routeInstanceId: string) => {
      navigate(`/collections/monitoring/timeline?date=${date}&routeId=${routeInstanceId}`)
    },
    [navigate],
  )

  const handleSort = useCallback(
    (col: SortColumn) => {
      if (sortColumn === col) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
      } else {
        setSortColumn(col)
        setSortDirection("asc")
      }
    },
    [sortColumn],
  )

  const SortIcon = ({ col }: { col: SortColumn }) => {
    if (sortColumn !== col)
      return <ArrowUpDown size={12} className="text-palette-jungleGreen400 opacity-60" />
    return sortDirection === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />
  }

  const handleExport = useCallback(() => {
    if (filteredRows.length === 0) return

    const headers = [
      "Date",
      "Day",
      "Route Code",
      "Depot",
      "Vehicle",
      "Planned Start",
      "Planned End",
      "Planned Duration (min)",
      "Actual Start",
      "Actual End",
      "Actual Duration (min)",
      "Duration Delta (min)",
      "Planned Injection Time",
      "Actual Injection Time",
      "Injection Delay (min)",
      "Injection Late >=15m",
      "Status",
      "Shipper Stops",
      "OTA15 Count",
      "OTA15 %",
      "OTA30 Count",
      "OTA30 %",
    ]

    const csvRows = filteredRows.map((r) => [
      r.date,
      r.dayOfWeek,
      r.routeCode,
      r.depot,
      r.vehicleReg,
      formatTime(r.plannedStart),
      formatTime(r.plannedEnd),
      r.plannedDurationMin?.toString() ?? "",
      formatTime(r.actualStart),
      formatTime(r.actualEnd),
      r.actualDurationMin?.toString() ?? "",
      r.durationDeltaMin?.toString() ?? "",
      formatTime(r.plannedInjectionTime),
      formatTime(r.actualInjectionTime),
      r.injectionDelayMin?.toString() ?? "",
      r.injectionDelayMin !== null ? (isInjectionLate(r) ? "Yes" : "No") : "",
      r.status,
      r.totalShipperStops.toString(),
      r.ota15Count.toString(),
      r.ota15Pct !== null ? r.ota15Pct.toFixed(0) : "",
      r.ota30Count.toString(),
      r.ota30Pct !== null ? r.ota30Pct.toFixed(0) : "",
    ])

    const csvContent = buildCsv(headers, csvRows)

    const rangeStr =
      dateRange?.from && dateRange?.to
        ? `${format(dateRange.from, "yyyy-MM-dd")}_to_${format(dateRange.to, "yyyy-MM-dd")}`
        : format(new Date(), "yyyy-MM-dd")
    downloadCsv(`route_performance_${rangeStr}.csv`, csvContent)
  }, [filteredRows, dateRange])

  const toggleDepot = (depot: string) => {
    setDepotFilter((prev) =>
      prev.includes(depot) ? prev.filter((d) => d !== depot) : [...prev, depot],
    )
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <div className="bg-white border-b border-palette-jungleGreen200 px-6 py-3 flex-shrink-0 flex items-center gap-3 flex-wrap">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
              <CalendarIcon size={12} />
              {dateRange?.from && dateRange?.to
                ? `${formatDayMonth(dateRange.from)} - ${formatDayMonthYear(dateRange.to)}`
                : "Select dates"}
            </Button>
          </PopoverTrigger>
          <PopoverContent variant="plain" align="start">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
              disabled={{ after: subDays(new Date(), 1) }}
            />
          </PopoverContent>
        </Popover>

        {availableDepots.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
                <Filter size={12} />
                Depot
                {depotFilter.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-[10px] px-1 py-0">
                    {depotFilter.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent variant="menu" align="start">
              <div className="space-y-1">
                {availableDepots.map((depot) => (
                  <label
                    key={depot}
                    className="flex items-center gap-2 px-2 py-1 hover:bg-palette-jungleGreen000 rounded cursor-pointer text-xs"
                  >
                    <Checkbox
                      checked={depotFilter.includes(depot)}
                      onCheckedChange={() => toggleDepot(depot)}
                    />
                    {depot}
                  </label>
                ))}
                {depotFilter.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs h-7 mt-1"
                    onClick={() => setDepotFilter([])}
                  >
                    Clear all
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}

        <div className="flex-1" />

        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          onClick={handleExport}
          disabled={filteredRows.length === 0}
        >
          <Download size={12} />
          Export CSV
        </Button>
      </div>

      <div className="bg-palette-jungleGreen000 border-b border-palette-jungleGreen200 px-6 py-3 flex-shrink-0">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card className="shadow-none border-palette-jungleGreen200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 text-palette-jungleGreen500 text-[10px] font-medium uppercase tracking-wide mb-1">
                <Clock size={10} />
                Total Routes
              </div>
              <div className="text-lg font-semibold text-palette-jungleGreen900">
                {summary.totalRoutes}
                {summary.routesWithActuals < summary.totalRoutes && (
                  <span className="text-xs text-palette-jungleGreen400 font-normal ml-1">
                    ({summary.routesWithActuals} with actuals)
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-none border-palette-jungleGreen200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 text-palette-jungleGreen500 text-[10px] font-medium uppercase tracking-wide mb-1">
                <TrendingUp size={10} />
                Avg Duration Delta
              </div>
              <div
                className={cn("text-lg font-semibold", getDeltaColor(summary.avgDurationDeltaMin))}
              >
                {summary.avgDurationDeltaMin !== null
                  ? `${summary.avgDurationDeltaMin > 0 ? "+" : ""}${formatMinutes(summary.avgDurationDeltaMin)}`
                  : "--"}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-none border-palette-jungleGreen200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 text-palette-jungleGreen500 text-[10px] font-medium uppercase tracking-wide mb-1">
                <PackageCheck size={10} />
                Injection &ge;15m Late
              </div>
              <div
                className={cn(
                  "text-lg font-semibold",
                  summary.injectionRoutesWithActual > 0
                    ? getLateCountColor(summary.injectionLate15)
                    : "text-palette-jungleGreen400",
                )}
              >
                {summary.injectionRoutesWithActual > 0 ? summary.injectionLate15 : "--"}
                {summary.injectionRoutesWithActual > 0 && (
                  <span className="text-xs text-palette-jungleGreen400 font-normal ml-1">
                    of {summary.injectionRoutesWithActual}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-none border-palette-jungleGreen200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 text-palette-jungleGreen500 text-[10px] font-medium uppercase tracking-wide mb-1">
                <Target size={10} />
                OTA15
              </div>
              <div className={cn("text-lg font-semibold", getOtaColor(summary.overallOta15Pct))}>
                {summary.overallOta15Pct !== null ? `${summary.overallOta15Pct.toFixed(0)}%` : "--"}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-none border-palette-jungleGreen200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 text-palette-jungleGreen500 text-[10px] font-medium uppercase tracking-wide mb-1">
                <Target size={10} />
                OTA30
              </div>
              <div className={cn("text-lg font-semibold", getOtaColor(summary.overallOta30Pct))}>
                {summary.overallOta30Pct !== null ? `${summary.overallOta30Pct.toFixed(0)}%` : "--"}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <LoadingState label="Loading route performance data..." className="h-48" />
        ) : filteredRows.length === 0 ? (
          <EmptyState className="flex h-48 items-center justify-center">
            {datesToFetch.length === 0
              ? "Select a date range to view route performance"
              : "No route instances found for the selected period"}
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  colSpan={6}
                  className="text-[10px] font-bold text-center bg-palette-jungleGreen100 text-palette-jungleGreen600 py-1 border-b-0"
                />
                <TableHead
                  colSpan={2}
                  className="text-[10px] font-bold text-center bg-green-100 text-green-800 py-1 border-b-0 border-x border-green-200"
                >
                  Planned
                </TableHead>
                <TableHead
                  colSpan={3}
                  className="text-[10px] font-bold text-center bg-rose-100 text-rose-800 py-1 border-b-0 border-x border-rose-200"
                >
                  Actual
                </TableHead>
                <TableHead
                  colSpan={3}
                  className="text-[10px] font-bold text-center bg-purple-100 text-purple-800 py-1 border-b-0 border-x border-purple-200"
                >
                  Injection
                </TableHead>
                <TableHead
                  colSpan={3}
                  className="text-[10px] font-bold text-center bg-palette-jungleGreen100 text-palette-jungleGreen600 py-1 border-b-0"
                />
              </TableRow>
              <TableRow className="bg-palette-jungleGreen000">
                <TableHead className="w-6 p-0" />
                <TableHead
                  className="text-[10px] font-semibold cursor-pointer hover:bg-palette-jungleGreen100 whitespace-nowrap"
                  onClick={() => handleSort("date")}
                >
                  <span className="flex items-center gap-1">
                    Date <SortIcon col="date" />
                  </span>
                </TableHead>
                <TableHead className="text-[10px] font-semibold whitespace-nowrap">Day</TableHead>
                <TableHead
                  className="text-[10px] font-semibold cursor-pointer hover:bg-palette-jungleGreen100 whitespace-nowrap"
                  onClick={() => handleSort("route")}
                >
                  <span className="flex items-center gap-1">
                    Route <SortIcon col="route" />
                  </span>
                </TableHead>
                <TableHead
                  className="text-[10px] font-semibold cursor-pointer hover:bg-palette-jungleGreen100 whitespace-nowrap"
                  onClick={() => handleSort("depot")}
                >
                  <span className="flex items-center gap-1">
                    Depot <SortIcon col="depot" />
                  </span>
                </TableHead>
                <TableHead className="text-[10px] font-semibold whitespace-nowrap">
                  Vehicle
                </TableHead>
                <TableHead className="text-[10px] font-semibold whitespace-nowrap bg-green-50 border-l border-green-200">
                  Times
                </TableHead>
                <TableHead
                  className="text-[10px] font-semibold cursor-pointer hover:bg-green-100 whitespace-nowrap bg-green-50 border-r border-green-200"
                  onClick={() => handleSort("plannedDuration")}
                >
                  <span className="flex items-center gap-1">
                    Duration <SortIcon col="plannedDuration" />
                  </span>
                </TableHead>
                <TableHead className="text-[10px] font-semibold whitespace-nowrap bg-rose-50 border-l border-rose-200">
                  Times
                </TableHead>
                <TableHead
                  className="text-[10px] font-semibold cursor-pointer hover:bg-rose-100 whitespace-nowrap bg-rose-50"
                  onClick={() => handleSort("actualDuration")}
                >
                  <span className="flex items-center gap-1">
                    Duration <SortIcon col="actualDuration" />
                  </span>
                </TableHead>
                <TableHead
                  className="text-[10px] font-semibold cursor-pointer hover:bg-rose-100 whitespace-nowrap bg-rose-50 border-r border-rose-200"
                  onClick={() => handleSort("durationDelta")}
                >
                  <span className="flex items-center gap-1">
                    &Delta; <SortIcon col="durationDelta" />
                  </span>
                </TableHead>
                <TableHead className="text-[10px] font-semibold whitespace-nowrap bg-purple-50 border-l border-purple-200">
                  Planned
                </TableHead>
                <TableHead className="text-[10px] font-semibold whitespace-nowrap bg-purple-50">
                  Actual
                </TableHead>
                <TableHead
                  className="text-[10px] font-semibold cursor-pointer hover:bg-purple-100 whitespace-nowrap bg-purple-50 border-r border-purple-200"
                  onClick={() => handleSort("injectionDelay")}
                >
                  <span className="flex items-center gap-1">
                    &Delta; <SortIcon col="injectionDelay" />
                  </span>
                </TableHead>
                <TableHead
                  className="text-[10px] font-semibold cursor-pointer hover:bg-palette-jungleGreen100 whitespace-nowrap"
                  onClick={() => handleSort("status")}
                >
                  <span className="flex items-center gap-1">
                    Status <SortIcon col="status" />
                  </span>
                </TableHead>
                <TableHead
                  className="text-[10px] font-semibold cursor-pointer hover:bg-palette-jungleGreen100 whitespace-nowrap"
                  onClick={() => handleSort("ota15")}
                >
                  <span className="flex items-center gap-1">
                    OTA15 <SortIcon col="ota15" />
                  </span>
                </TableHead>
                <TableHead
                  className="text-[10px] font-semibold cursor-pointer hover:bg-palette-jungleGreen100 whitespace-nowrap"
                  onClick={() => handleSort("ota30")}
                >
                  <span className="flex items-center gap-1">
                    OTA30 <SortIcon col="ota30" />
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map((row) => {
                const isExpanded = expandedRows.has(row.routeInstanceId)
                return (
                  <React.Fragment key={row.routeInstanceId}>
                    <TableRow
                      className="hover:bg-palette-jungleGreen000 cursor-pointer"
                      onClick={() => toggleRow(row.routeInstanceId)}
                    >
                      <TableCell className="w-6 p-0 pl-2 text-palette-jungleGreen400">
                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{row.date}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap text-palette-jungleGreen500">
                        {row.dayOfWeek.slice(0, 3)}
                      </TableCell>
                      <TableCell className="text-xs font-medium whitespace-nowrap">
                        {row.routeCode}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap text-palette-jungleGreen500">
                        {row.depot}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap text-palette-jungleGreen500">
                        {row.vehicleReg || "--"}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap bg-green-50/60 border-l border-green-100">
                        {formatTime(row.plannedStart)} – {formatTime(row.plannedEnd)}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap bg-green-50/60 border-r border-green-100">
                        {formatMinutes(row.plannedDurationMin)}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap bg-rose-50/60 border-l border-rose-100">
                        {row.actualStart
                          ? `${formatTime(row.actualStart)} – ${formatTime(row.actualEnd)}`
                          : "--"}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap bg-rose-50/60">
                        {formatMinutes(row.actualDurationMin)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-xs whitespace-nowrap bg-rose-50/60 border-r border-rose-100",
                          getDeltaColor(row.durationDeltaMin),
                        )}
                      >
                        {row.durationDeltaMin !== null
                          ? `${row.durationDeltaMin > 0 ? "+" : ""}${formatMinutes(row.durationDeltaMin)}`
                          : "--"}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap bg-purple-50/60 border-l border-purple-100">
                        {formatTime(row.plannedInjectionTime)}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap bg-purple-50/60">
                        {formatTime(row.actualInjectionTime)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-xs whitespace-nowrap bg-purple-50/60 border-r border-purple-100",
                          getDeltaColor(row.injectionDelayMin),
                        )}
                      >
                        {row.injectionDelayMin !== null
                          ? `${row.injectionDelayMin > 0 ? "+" : ""}${formatMinutes(row.injectionDelayMin)}`
                          : "--"}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {getStatusBadge(row.status)}
                      </TableCell>
                      <TableCell
                        className={cn("text-xs whitespace-nowrap", getOtaColor(row.ota15Pct))}
                      >
                        {row.ota15Pct !== null ? `${row.ota15Pct.toFixed(0)}%` : "--"}
                        {row.shipperStopsWithActual > 0 && (
                          <span className="text-palette-jungleGreen400 ml-0.5 text-[10px]">
                            ({row.ota15Count}/{row.shipperStopsWithActual})
                          </span>
                        )}
                      </TableCell>
                      <TableCell
                        className={cn("text-xs whitespace-nowrap", getOtaColor(row.ota30Pct))}
                      >
                        {row.ota30Pct !== null ? `${row.ota30Pct.toFixed(0)}%` : "--"}
                        {row.shipperStopsWithActual > 0 && (
                          <span className="text-palette-jungleGreen400 ml-0.5 text-[10px]">
                            ({row.ota30Count}/{row.shipperStopsWithActual})
                          </span>
                        )}
                      </TableCell>
                    </TableRow>

                    {isExpanded && (
                      <TableRow className="bg-palette-jungleGreen000 hover:bg-palette-jungleGreen000">
                        <TableCell colSpan={17} className="p-0">
                          <div className="px-8 py-3 border-t border-palette-jungleGreen200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[11px] font-semibold text-palette-jungleGreen600 uppercase tracking-wide">
                                Stop breakdown — {row.routeCode}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-[11px] text-blue-600 hover:text-blue-700 gap-1 px-2"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleOpenInMonitoring(row.date, row.routeInstanceId)
                                }}
                              >
                                <ExternalLink size={11} />
                                Open in Monitoring
                              </Button>
                            </div>

                            {row.stops.length === 0 ? (
                              <p className="text-xs text-palette-jungleGreen400">
                                No stop data available
                              </p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-[10px] text-palette-jungleGreen500 border-b border-palette-jungleGreen200">
                                    <th className="text-left font-medium pb-1 pr-4 w-6">#</th>
                                    <th className="text-left font-medium pb-1 pr-4">Stop</th>
                                    <th className="text-left font-medium pb-1 pr-4">Type</th>
                                    <th className="text-left font-medium pb-1 pr-4 bg-green-50/60 px-2">
                                      Planned Arrival
                                    </th>
                                    <th className="text-left font-medium pb-1 pr-4 bg-rose-50/60 px-2">
                                      Actual Arrival
                                    </th>
                                    <th className="text-left font-medium pb-1 bg-rose-50/60 px-2">
                                      Delay
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.stops.map((stop) => (
                                    <tr
                                      key={stop.id}
                                      className="border-b border-palette-jungleGreen100 last:border-0"
                                    >
                                      <td className="py-1 pr-4 text-palette-jungleGreen400">
                                        {stop.sequence}
                                      </td>
                                      <td className="py-1 pr-4 font-medium text-palette-jungleGreen800">
                                        {stop.name || "--"}
                                      </td>
                                      <td className="py-1 pr-4">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-palette-jungleGreen100 text-palette-jungleGreen600">
                                          {stop.stopType.replace(/_/g, " ")}
                                        </span>
                                      </td>
                                      <td className="py-1 pr-4 bg-green-50/40 px-2">
                                        {formatTime(stop.plannedArrival)}
                                      </td>
                                      <td className="py-1 pr-4 bg-rose-50/40 px-2">
                                        {formatTime(stop.actualArrival)}
                                      </td>
                                      <td
                                        className={cn(
                                          "py-1 bg-rose-50/40 px-2",
                                          getDeltaColor(stop.delayMin),
                                        )}
                                      >
                                        {stop.delayMin !== null
                                          ? `${stop.delayMin > 0 ? "+" : ""}${formatMinutes(stop.delayMin)}`
                                          : "--"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}

export default RoutePerformance
